import type { Entity, Structure, Unit } from "model/OrganizationalStructure";
import type { StaffItem } from "services/api/company/staff.service";

export type OrganizationPerson = StaffItem;

export interface OrganizationUnit extends Unit {
  readonly entityId: number;
}

interface FilterWorkersParams {
  readonly query: string;
  readonly workers: readonly OrganizationPerson[];
}

interface FilterOrganizationUnitsParams {
  readonly selectedTeamIds: readonly string[];
  readonly selectedUnitId?: number | null;
  readonly units: readonly OrganizationUnit[];
}

interface OrganizationFilterOption {
  readonly entityId: number;
  readonly teams: ReadonlyArray<{ readonly id: number }>;
}

interface ResolveOrganizationFilterSelectionParams {
  readonly activeEntityId: number | null;
  readonly currentTeamIds: readonly string[];
  readonly nextTeamIds: readonly string[];
  readonly options: readonly OrganizationFilterOption[];
}

interface OrganizationFilterSelection {
  readonly entityId: number | null;
  readonly teamIds: string[];
}

interface FindEntityForTeamParams {
  readonly fallbackEntityId: number | null;
  readonly options: readonly OrganizationFilterOption[];
  readonly teamId?: string;
}

interface GetEntityTeamIdsParams {
  readonly entityId: number;
  readonly options: readonly OrganizationFilterOption[];
}

interface GetEntityDisplayNameParams {
  readonly name: string;
  readonly unhandledName: string;
}

export function getEntityDisplayName({
  name,
  unhandledName,
}: GetEntityDisplayNameParams): string {
  return name.trim().toLowerCase() === "unhandled" ? unhandledName : name;
}

function findEntityForTeam({
  fallbackEntityId,
  options,
  teamId,
}: FindEntityForTeamParams): number | null {
  return (
    options.find((option) =>
      option.teams.some((team) => String(team.id) === teamId),
    )?.entityId ?? fallbackEntityId
  );
}

function getEntityTeamIds({
  entityId,
  options,
}: GetEntityTeamIdsParams): Set<string> {
  return new Set(
    options
      .filter((option) => option.entityId === entityId)
      .flatMap((option) => option.teams.map((team) => String(team.id))),
  );
}

export function resolveOrganizationFilterSelection({
  activeEntityId,
  currentTeamIds,
  nextTeamIds,
  options,
}: ResolveOrganizationFilterSelectionParams): OrganizationFilterSelection {
  const addedTeamId = nextTeamIds.find(
    (teamId) => !currentTeamIds.includes(teamId),
  );
  const nextEntityId = findEntityForTeam({
    fallbackEntityId: activeEntityId,
    options,
    teamId: addedTeamId,
  });
  if (nextEntityId == null || nextEntityId === activeEntityId) {
    return { entityId: activeEntityId, teamIds: [...nextTeamIds] };
  }

  const availableTeamIds = getEntityTeamIds({
    entityId: nextEntityId,
    options,
  });
  return {
    entityId: nextEntityId,
    teamIds: nextTeamIds.filter((teamId) => availableTeamIds.has(teamId)),
  };
}

interface GetUnitTeamCountParams {
  readonly entityId: number;
  readonly unitId: number;
  readonly units: readonly OrganizationUnit[];
}

interface GetUnitPersonCountParams {
  readonly unit: Unit;
  readonly workers: readonly OrganizationPerson[] | null;
}

interface HasOtherTeamMembershipParams {
  readonly currentTeamId: number;
  readonly worker: OrganizationPerson;
}

export function filterOrganizationUnits({
  selectedTeamIds,
  selectedUnitId,
  units,
}: FilterOrganizationUnitsParams): OrganizationUnit[] {
  if (selectedUnitId != null) {
    return units.filter((unit) => unit.id === selectedUnitId);
  }
  if (selectedTeamIds.length === 0) return [...units];
  const selectedTeamIdSet = new Set(selectedTeamIds);

  return units.flatMap((unit) => {
    const matchingTeams = unit.workGroups.filter((team) =>
      selectedTeamIdSet.has(String(team.id)),
    );

    return matchingTeams.length > 0
      ? [{ ...unit, workGroups: matchingTeams }]
      : [];
  });
}

export function flattenOrganizationUnits(
  structure: Structure,
): OrganizationUnit[] {
  return structure.entities.flatMap((entity: Entity) => {
    return entity.units.map((unit: Unit) => ({
      ...unit,
      entityId: entity.id,
    }));
  });
}

export function getTeamMemberCounts(
  workers: readonly OrganizationPerson[],
): ReadonlyMap<number, number> {
  const workerIdsByTeamId = new Map<number, Set<number>>();
  workers.forEach((worker) => {
    const teamIds = new Set(worker.workGroups.map((team) => team.workGroupId));
    if (worker.workGroupId != null) teamIds.add(worker.workGroupId);
    teamIds.forEach((teamId) => {
      const workerIds = workerIdsByTeamId.get(teamId) ?? new Set<number>();
      workerIds.add(worker.workerId);
      workerIdsByTeamId.set(teamId, workerIds);
    });
  });
  return new Map(
    Array.from(workerIdsByTeamId, ([teamId, workerIds]) => [
      teamId,
      workerIds.size,
    ]),
  );
}

export function getUnitPersonCount({
  unit,
  workers,
}: GetUnitPersonCountParams): number {
  if (workers == null) {
    return new Set(
      unit.workGroups.flatMap((team) =>
        (team.workers ?? []).map((worker) => worker.workerId),
      ),
    ).size;
  }
  const teamIds = new Set(unit.workGroups.map((team) => team.id));
  return new Set(
    workers
      .filter(
        (worker) =>
          (worker.workGroupId != null && teamIds.has(worker.workGroupId)) ||
          worker.workGroups.some((workGroup) =>
            teamIds.has(workGroup.workGroupId),
          ),
      )
      .map((worker) => worker.workerId),
  ).size;
}

export function getUnitTeamCount({
  entityId,
  unitId,
  units,
}: GetUnitTeamCountParams): number {
  return (
    units.find((unit) => unit.entityId === entityId && unit.id === unitId)
      ?.workGroups.length ?? 0
  );
}

export function getWorkerName(worker: OrganizationPerson): string {
  return `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
}

export function getWorkerRole(worker: OrganizationPerson): string {
  return worker.role ?? "";
}

export function isWorkerInactive(worker: OrganizationPerson): boolean {
  return worker.status !== "Active";
}

export function hasOtherTeamMembership({
  currentTeamId,
  worker,
}: HasOtherTeamMembershipParams): boolean {
  return worker.workGroups.some(
    (workGroup) => workGroup.workGroupId !== currentTeamId,
  );
}

export function mergeWorkersById(
  current: readonly OrganizationPerson[],
  next: readonly OrganizationPerson[],
): OrganizationPerson[] {
  return Array.from(
    new Map(
      [...current, ...next].map((worker) => [worker.workerId, worker]),
    ).values(),
  );
}

export function filterWorkers({
  query,
  workers,
}: FilterWorkersParams): OrganizationPerson[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return [...workers];

  return workers.filter((worker) => {
    const teamNames = worker.workGroups
      .map((team) => team.workGroupName ?? "")
      .join(" ");
    const unitNames = worker.workGroups
      .map((team) => team.companyUnitName ?? "")
      .join(" ");
    const searchable = `${getWorkerName(worker)} ${getWorkerRole(worker)} ${teamNames} ${unitNames}`;
    return searchable.toLocaleLowerCase().includes(normalizedQuery);
  });
}
